import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { 
  CalendarIcon, 
  FileText, 
  Loader2,
  ArrowRight,
  Users,
  Printer
} from 'lucide-react';

const ExportAllStatementsPage = () => {
  const navigate = useNavigate();
  const { API_URL, getAuthHeader } = useAuth();
  const { language } = useLanguage();
  
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [statementsData, setStatementsData] = useState([]);
  
  const today = new Date();
  const [startDate, setStartDate] = useState(startOfWeek(today, { weekStartsOn: 0 }));
  const [endDate, setEndDate] = useState(endOfWeek(today, { weekStartsOn: 0 }));
  const [datePreset, setDatePreset] = useState('this_week');

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (selectAll) {
      setSelectedCustomers(customers.map(c => c.id));
    }
  }, [customers, selectAll]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/customers`, { headers: getAuthHeader() });
      setCustomers(response.data);
      setSelectedCustomers(response.data.map(c => c.id));
    } catch (error) {
      toast.error('حدث خطأ في تحميل العملاء');
    } finally {
      setLoading(false);
    }
  };

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    const now = new Date();
    switch (preset) {
      case 'this_week':
        setStartDate(startOfWeek(now, { weekStartsOn: 0 }));
        setEndDate(endOfWeek(now, { weekStartsOn: 0 }));
        break;
      case 'last_week':
        const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        setStartDate(startOfWeek(lastWeek, { weekStartsOn: 0 }));
        setEndDate(endOfWeek(lastWeek, { weekStartsOn: 0 }));
        break;
      case 'this_month':
        setStartDate(startOfMonth(now));
        setEndDate(endOfMonth(now));
        break;
      case 'last_month':
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        setStartDate(startOfMonth(lastMonth));
        setEndDate(endOfMonth(lastMonth));
        break;
      default:
        break;
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    setSelectedCustomers(checked ? customers.map(c => c.id) : []);
  };

  const handleCustomerToggle = (customerId) => {
    setSelectedCustomers(prev => {
      if (prev.includes(customerId)) {
        setSelectAll(false);
        return prev.filter(id => id !== customerId);
      } else {
        const newSelection = [...prev, customerId];
        if (newSelection.length === customers.length) setSelectAll(true);
        return newSelection;
      }
    });
  };

  const generateStatements = async () => {
    if (selectedCustomers.length === 0) {
      toast.error('الرجاء اختيار عميل واحد على الأقل');
      return;
    }
    setGenerating(true);
    const start = format(startDate, 'yyyy-MM-dd');
    const end = format(endDate, 'yyyy-MM-dd');
    const statements = [];
    try {
      for (const customerId of selectedCustomers) {
        try {
          const response = await axios.get(
            `${API_URL}/api/customers/${customerId}/statement?start_date=${start}&end_date=${end}`,
            { headers: getAuthHeader() }
          );
          if (response.data && (response.data.orders?.length > 0 || response.data.payments?.length > 0 || response.data.previous_balance > 0)) {
            statements.push(response.data);
          }
        } catch (error) {}
      }
      if (statements.length === 0) {
        toast.warning('لا توجد بيانات للعملاء المحددين في الفترة المختارة');
      } else {
        setStatementsData(statements);
        toast.success(`تم تحميل ${statements.length} كشف حساب`);
      }
    } catch (error) {
      toast.error('حدث خطأ في توليد الكشوفات');
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US').format(amount || 0);

  const dayNames = {
    ar: ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
    de: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  };

  const getDayName = (dateStr) => {
    const date = new Date(dateStr);
    return dayNames[language]?.[date.getDay()] || dayNames.ar[date.getDay()];
  };

  const handlePrint = () => {
    const dateRange = `${format(startDate, 'yyyy/MM/dd')} - ${format(endDate, 'yyyy/MM/dd')}`;
    
    let printContent = '';
    statementsData.forEach((statement, index) => {
      const pageBreak = index < statementsData.length - 1 ? 'page-break-after: always;' : '';
      
      let ordersHTML = '';
      if (statement.orders && statement.orders.length > 0) {
        ordersHTML = `
          <div style="margin-bottom: 4mm;">
            <h4 style="font-size: 10pt; font-weight: bold; margin: 0 0 2mm 0; background: #f5f5f4; padding: 2mm; border-radius: 1mm;">الطلبات</h4>
            <table style="width: 100%; border-collapse: collapse; font-size: 8pt;">
              <thead>
                <tr style="background: #fafaf9;">
                  <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: right;">التاريخ</th>
                  <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: right;">اليوم</th>
                  <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: right;">المنتجات</th>
                  <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: right;">المبلغ</th>
                  <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: right;">الفعلي</th>
                </tr>
              </thead>
              <tbody>
                ${statement.orders.map(order => {
                  const hasPartial = order.items?.some(item => item.delivered_quantity !== undefined && item.delivered_quantity !== null && item.delivered_quantity !== item.quantity);
                  const products = order.items?.map(item => {
                    const isPartial = item.delivered_quantity !== undefined && item.delivered_quantity !== null && item.delivered_quantity !== item.quantity;
                    return `<span style="color: ${isPartial ? '#dc2626' : 'inherit'}">${item.product_name}: ${item.delivered_quantity ?? item.quantity}${isPartial ? ` (${item.quantity})` : ''}</span>`;
                  }).join(' | ') || '';
                  return `
                    <tr style="background: ${hasPartial ? '#fef2f2' : 'white'};">
                      <td style="border: 1px solid #d6d3d1; padding: 1.5mm;">${order.date}</td>
                      <td style="border: 1px solid #d6d3d1; padding: 1.5mm;">${getDayName(order.date)}</td>
                      <td style="border: 1px solid #d6d3d1; padding: 1.5mm; font-size: 7pt;">${products}</td>
                      <td style="border: 1px solid #d6d3d1; padding: 1.5mm;">${formatCurrency(order.total)}</td>
                      <td style="border: 1px solid #d6d3d1; padding: 1.5mm; font-weight: bold;">${formatCurrency(order.actual_total ?? order.total)}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
        `;
      }

      let paymentsHTML = '';
      if (statement.payments && statement.payments.length > 0) {
        paymentsHTML = `
          <div style="margin-bottom: 4mm;">
            <h4 style="font-size: 10pt; font-weight: bold; margin: 0 0 2mm 0; background: #f0fdf4; padding: 2mm; border-radius: 1mm; color: #15803d;">المدفوعات</h4>
            <table style="width: 100%; border-collapse: collapse; font-size: 8pt;">
              <thead>
                <tr style="background: #f0fdf4;">
                  <th style="border: 1px solid #bbf7d0; padding: 2mm; text-align: right;">التاريخ</th>
                  <th style="border: 1px solid #bbf7d0; padding: 2mm; text-align: right;">المبلغ</th>
                  <th style="border: 1px solid #bbf7d0; padding: 2mm; text-align: right;">ملاحظات</th>
                </tr>
              </thead>
              <tbody>
                ${statement.payments.map(payment => `
                  <tr>
                    <td style="border: 1px solid #bbf7d0; padding: 1.5mm;">${payment.date}</td>
                    <td style="border: 1px solid #bbf7d0; padding: 1.5mm; color: #16a34a; font-weight: bold;">-${formatCurrency(payment.amount)}</td>
                    <td style="border: 1px solid #bbf7d0; padding: 1.5mm;">${payment.notes || '-'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
      }

      printContent += `
        <div style="${pageBreak} padding: 5mm;">
          <div style="text-align: center; margin-bottom: 6mm; border-bottom: 2px solid #d97706; padding-bottom: 4mm;">
            <h2 style="font-size: 16pt; font-weight: bold; margin: 0 0 2mm 0; color: #1c1917;">كشف حساب</h2>
            <h3 style="font-size: 13pt; font-weight: bold; margin: 0 0 2mm 0; color: #d97706;">${statement.customer_name}</h3>
            <p style="font-size: 9pt; color: #57534e; margin: 0;">${dateRange}</p>
          </div>
          
          ${statement.previous_balance > 0 ? `
            <div style="margin-bottom: 4mm; padding: 2mm 3mm; background: #fff7ed; border: 1px solid #fed7aa; border-radius: 2mm;">
              <div style="display: flex; justify-content: space-between;">
                <span style="font-weight: bold; color: #c2410c;">الرصيد السابق:</span>
                <span style="font-weight: bold; color: #c2410c;">${formatCurrency(statement.previous_balance)}</span>
              </div>
            </div>
          ` : ''}
          
          ${ordersHTML}
          ${paymentsHTML}
          
          <div style="margin-top: 4mm; padding: 3mm; background: #f5f5f4; border-radius: 2mm;">
            <div style="display: flex; justify-content: space-around; text-align: center;">
              <div>
                <p style="margin: 0 0 1mm 0; font-size: 8pt; color: #78716c;">إجمالي الطلبات</p>
                <p style="margin: 0; font-weight: bold; font-size: 11pt;">${formatCurrency(statement.total_orders)}</p>
              </div>
              <div>
                <p style="margin: 0 0 1mm 0; font-size: 8pt; color: #78716c;">الفعلي</p>
                <p style="margin: 0; font-weight: bold; font-size: 11pt; color: #d97706;">${formatCurrency(statement.actual_total_orders ?? statement.total_orders)}</p>
              </div>
              <div>
                <p style="margin: 0 0 1mm 0; font-size: 8pt; color: #78716c;">المدفوعات</p>
                <p style="margin: 0; font-weight: bold; font-size: 11pt; color: #16a34a;">-${formatCurrency(statement.total_payments)}</p>
              </div>
              <div style="background: #fef3c7; padding: 2mm 4mm; border-radius: 2mm;">
                <p style="margin: 0 0 1mm 0; font-size: 8pt; color: #b45309; font-weight: bold;">الرصيد المتبقي</p>
                <p style="margin: 0; font-weight: bold; font-size: 12pt; color: #b45309;">${formatCurrency(statement.current_balance)}</p>
              </div>
            </div>
          </div>
        </div>
      `;
    });

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>كشوفات الحسابات</title>
        <style>
          @page { size: A4 portrait; margin: 8mm 10mm; }
          * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body { font-family: Arial, sans-serif; font-size: 10pt; direction: rtl; }
          table { border-collapse: collapse; width: 100%; }
        </style>
      </head>
      <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
          <ArrowRight className="w-4 h-4" />
          رجوع
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-stone-900">تصدير جميع الحسابات</h1>
          <p className="text-stone-500">تصدير كشوفات حساب العملاء كملف PDF</p>
        </div>
      </div>

      {/* Date Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5" />
            اختر الفترة الزمنية
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="space-y-2">
              <Label>فترة محددة مسبقاً</Label>
              <Select value={datePreset} onValueChange={handleDatePresetChange}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="this_week">هذا الأسبوع</SelectItem>
                  <SelectItem value="last_week">الأسبوع الماضي</SelectItem>
                  <SelectItem value="this_month">هذا الشهر</SelectItem>
                  <SelectItem value="last_month">الشهر الماضي</SelectItem>
                  <SelectItem value="custom">تحديد يدوي</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <div className="space-y-2">
                <Label>من</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start text-right font-normal min-w-[150px]">
                      <CalendarIcon className="ms-2 h-4 w-4" />
                      {format(startDate, 'yyyy/MM/dd')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={startDate} onSelect={(date) => { date && setStartDate(date); setDatePreset('custom'); }} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label>إلى</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start text-right font-normal min-w-[150px]">
                      <CalendarIcon className="ms-2 h-4 w-4" />
                      {format(endDate, 'yyyy/MM/dd')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={endDate} onSelect={(date) => { date && setEndDate(date); setDatePreset('custom'); }} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customer Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              اختر العملاء
            </span>
            <div className="flex items-center gap-2">
              <Checkbox id="select-all" checked={selectAll} onCheckedChange={handleSelectAll} />
              <Label htmlFor="select-all" className="text-sm font-normal cursor-pointer">
                تحديد الكل ({customers.length})
              </Label>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 max-h-[300px] overflow-y-auto">
              {customers.map((customer) => (
                <div
                  key={customer.id}
                  className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                    selectedCustomers.includes(customer.id) ? 'bg-amber-50 border-amber-300' : 'bg-white border-stone-200 hover:bg-stone-50'
                  }`}
                  onClick={() => handleCustomerToggle(customer.id)}
                >
                  <Checkbox checked={selectedCustomers.includes(customer.id)} onCheckedChange={() => handleCustomerToggle(customer.id)} />
                  <span className="text-sm truncate">{customer.name}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate Button */}
      <div className="flex justify-center">
        <Button
          onClick={generateStatements}
          disabled={generating || selectedCustomers.length === 0}
          className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg gap-3"
        >
          {generating ? (
            <><Loader2 className="w-5 h-5 animate-spin" />جاري التحميل...</>
          ) : (
            <><FileText className="w-5 h-5" />توليد الكشوفات ({selectedCustomers.length} عميل)</>
          )}
        </Button>
      </div>

      {/* Results */}
      {statementsData.length > 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-green-800">تم تحميل {statementsData.length} كشف حساب</p>
                  <p className="text-sm text-green-600">جاهز للطباعة أو التحميل كـ PDF</p>
                </div>
              </div>
              <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700 gap-2 px-6">
                <Printer className="w-5 h-5" />
                طباعة / تحميل PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExportAllStatementsPage;
